import { NAVIGATION_LINK_IDS } from '~/consts/navigation';

import { UserType } from '~/types/responses/users';

import { canAccessCxDashboard } from './canAccessCxDashboard';
import { canAccessLab } from './canAccessLab';
import featureToggleUtils from './featureToggle';

type NavigationUser = App.User | UserType | null | undefined;
const HIDDEN_LE_BUSINESS_TRAVELLER_NAVIGATION_IDS = new Set<string>([
  NAVIGATION_LINK_IDS.SUPPORT_ASSISTANT,
  NAVIGATION_LINK_IDS.COMMERCIAL_OPS,
  NAVIGATION_LINK_IDS.SOCIETE,
]);

export function userCanAccessNavigationLink(navLink: App.NavigationLink, user: NavigationUser) {
  if (!user) {
    return false;
  }

  return (
    featureToggleUtils.availableToShow(navLink.featureToggle) &&
    (!navLink.requiredRoles?.size || user.roles.some((role) => navLink.requiredRoles.has(role))) &&
    (navLink.id !== NAVIGATION_LINK_IDS.LAB || canAccessLab(user)) &&
    (navLink.id !== NAVIGATION_LINK_IDS.CX_DASHBOARD || canAccessCxDashboard(user))
  );
}

export function filterNavigationLinksForUser(
  navLinksList: Array<App.NavigationLink>,
  user: NavigationUser,
): Array<App.NavigationLink> {
  return navLinksList.reduce<Array<App.NavigationLink>>((acc, navLink) => {
    if (!userCanAccessNavigationLink(navLink, user)) {
      return acc;
    }

    if (!navLink.subLinks?.length) {
      return acc.concat(navLink);
    }

    return acc.concat({
      ...navLink,
      subLinks: filterNavigationLinksForUser(navLink.subLinks, user),
    });
  }, []);
}

export function canShowNavigationLinkForTenant(navLink: App.NavigationLink, tenant: App.Tenant) {
  if (navLink.tenants && !navLink.tenants.has(tenant.value)) {
    return false;
  }

  if (navLink.id === NAVIGATION_LINK_IDS.BUSINESS || navLink.id === NAVIGATION_LINK_IDS.INVOICE_TOOLS) {
    return tenant.brand === 'lebusinesstraveller';
  }

  if (tenant.brand === 'lebusinesstraveller' && HIDDEN_LE_BUSINESS_TRAVELLER_NAVIGATION_IDS.has(navLink.id ?? '')) {
    return false;
  }

  return true;
}

export function filterNavigationLinksForTenant(
  navLinksList: Array<App.NavigationLink>,
  tenant: App.Tenant,
): Array<App.NavigationLink> {
  return navLinksList.reduce<Array<App.NavigationLink>>((acc, navLink) => {
    if (!canShowNavigationLinkForTenant(navLink, tenant)) {
      return acc;
    }

    return acc.concat({
      ...navLink,
      subLinks: navLink.subLinks ? filterNavigationLinksForTenant(navLink.subLinks, tenant) : undefined,
    });
  }, []);
}

export function isNavigationPathSelected(pathname: string, href?: string) {
  if (!href) {
    return false;
  }

  if (href === '/') {
    return pathname === '/';
  }

  return pathname === href || pathname.startsWith(`${href}/`);
}
